// src/pages/processifyd/ProcessifydMappings.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFolderOpen,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faLink,
} from "@fortawesome/free-solid-svg-icons";

const ProcessifydMappings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("processGroup"); // processGroup | stateGroup | stateVariable

  // Data States
  const [processGroupMappings, setProcessGroupMappings] = useState([]);
  const [stateGroupMappings, setStateGroupMappings] = useState([]);
  const [stateVariableMappings, setStateVariableMappings] = useState([]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [modalType, setModalType] = useState("processGroup");

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  // Initial Forms
  const initialProcessGroupForm = {
    processName: "",
    groupName: "",
    processGroupRemarks: "",
    client: "",
    active: "Yes",
  };

  const initialStateGroupForm = {
    stateName: "",
    groupName: "",
    stateGroupRemarks: "",
    client: "",
    active: "Yes",
  };

  const initialStateVariableForm = {
    stateName: "",
    variableName: "",
    stateVariableRemarks: "",
    client: "",
    active: "Yes",
  };

  const [formData, setFormData] = useState(initialProcessGroupForm);

  // Sample Demo Data
  const demoProcessGroup = [
    {
      id: 1,
      processName: "CSAT Process",
      groupName: "Branch",
      processGroupRemarks: "CSAT Process is for the Branch",
      client: "21st Century Financial Services",
      active: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "12/12/23 15:32",
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: "12/12/23 15:32",
    },
  ];

  const demoStateGroup = [
    {
      id: 101,
      stateName: "CAPA Initiated",
      groupName: "Branch",
      stateGroupRemarks: "CAPA Initiated State of CSAT Process is for the Branch",
      client: "21st Century Financial Services",
      active: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "12/12/23 15:32",
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: "12/12/23 15:32",
    },
  ];

  const demoStateVariable = [
    {
      id: 201,
      stateName: "CAPA Initiated",
      variableName: "AWB Number",
      stateVariableRemarks: "The variable is used to store data for the AWB Number when a letter is sent to the person",
      client: "21st Century Financial Services",
      active: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "12/12/23 15:32",
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: "12/12/23 15:32",
    },
  ];

  const loadSample = () => {
    if (activeTab === "processGroup") setProcessGroupMappings(demoProcessGroup);
    else if (activeTab === "stateGroup") setStateGroupMappings(demoStateGroup);
    else setStateVariableMappings(demoStateVariable);
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setModalType(activeTab);
    setFormData(
      activeTab === "processGroup" ? initialProcessGroupForm :
      activeTab === "stateGroup" ? initialStateGroupForm :
      initialStateVariableForm
    );
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setModalType(activeTab);
    
    setFormData(
      activeTab === "processGroup" ? {
        processName: item.processName || "",
        groupName: item.groupName || "",
        processGroupRemarks: item.processGroupRemarks || "",
        client: item.client || "",
        active: item.active || "Yes",
      } : activeTab === "stateGroup" ? {
        stateName: item.stateName || "",
        groupName: item.groupName || "",
        stateGroupRemarks: item.stateGroupRemarks || "",
        client: item.client || "",
        active: item.active || "Yes",
      } : {
        stateName: item.stateName || "",
        variableName: item.variableName || "",
        stateVariableRemarks: item.stateVariableRemarks || "",
        client: item.client || "",
        active: item.active || "Yes",
      }
    );
    setShowModal(true);
  };

  const handleDelete = (id) => {
    const typeName = activeTab === "processGroup" ? "Process Group Mapping" :
                     activeTab === "stateGroup" ? "State Group Mapping" : "State Variable Mapping";
    
    if (!window.confirm(`Delete this ${typeName}?`)) return;

    if (activeTab === "processGroup") setProcessGroupMappings(prev => prev.filter(i => i.id !== id));
    else if (activeTab === "stateGroup") setStateGroupMappings(prev => prev.filter(i => i.id !== id));
    else setStateVariableMappings(prev => prev.filter(i => i.id !== id));
  };

  const handleOpen = (item) => {
    const base = activeTab === "processGroup" ? "processgroup" :
                 activeTab === "stateGroup" ? "stategroup" : "statevariable";
    navigate(`/processifyd-${base}-mapping/${item.id}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newItem = {
      id: editingItem?.id || Date.now(),
      ...formData,
      createdBy: editingItem?.createdBy || "Sanjeev Kumar",
      createdDate: editingItem?.createdDate || new Date().toLocaleString("en-IN"),
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: new Date().toLocaleString("en-IN"),
    };

    if (modalType === "processGroup") {
      setProcessGroupMappings(prev => editingItem 
        ? prev.map(i => i.id === editingItem.id ? newItem : i)
        : [...prev, newItem]);
    } else if (modalType === "stateGroup") {
      setStateGroupMappings(prev => editingItem 
        ? prev.map(i => i.id === editingItem.id ? newItem : i)
        : [...prev, newItem]);
    } else {
      setStateVariableMappings(prev => editingItem 
        ? prev.map(i => i.id === editingItem.id ? newItem : i)
        : [...prev, newItem]);
    }

    setShowModal(false);
    setFormData(modalType === "processGroup" ? initialProcessGroupForm :
                modalType === "stateGroup" ? initialStateGroupForm : initialStateVariableForm);
  };

  // Current Data & Filtering
  const currentData = activeTab === "processGroup" ? processGroupMappings :
                      activeTab === "stateGroup" ? stateGroupMappings : stateVariableMappings;

  const filtered = currentData.filter(item => {
    const term = search.toLowerCase();
    if (activeTab === "processGroup") {
      return item.processName?.toLowerCase().includes(term) || 
             item.groupName?.toLowerCase().includes(term);
    } else if (activeTab === "stateGroup") {
      return item.stateName?.toLowerCase().includes(term) || 
             item.groupName?.toLowerCase().includes(term);
    } else {
      return item.stateName?.toLowerCase().includes(term) || 
             item.variableName?.toLowerCase().includes(term);
    }
  });

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            🔗 Processifyd Mappings
          </div>
          <div className="text-xs text-gray-400">
            Process • State • Variable Group Mappings
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn && (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button onClick={handleLogout} className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm">⏻</button>
            </>
          )}
        </div>

        <button className="md:hidden text-2xl text-cyan-300" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-4">
          <div className="flex flex-col gap-2">
            {["processGroup", "stateGroup", "raciPerson"].map(tab => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); setCurrentPage(1); setSearch(""); setIsMobileMenuOpen(false); }}
                className={`px-4 py-3 text-left rounded-lg ${activeTab === tab ? "bg-cyan-600 text-white" : "text-gray-400"}`}
              >
                {tab === "processGroup" && "Process and Group Mapping"}
                {tab === "stateGroup" && "State and Group Mapping"}
                {tab === "raciPerson" && "RACI and Person Mapping"}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="min-h-[75vh]">
        {/* Tabs */}
        <div className="mt-6 flex border-b border-gray-700 overflow-x-auto">
          <button onClick={() => { setActiveTab("processGroup"); setCurrentPage(1); setSearch(""); }}
            className={`px-6 py-3 font-medium whitespace-nowrap flex items-center gap-2 border-b-2 ${activeTab === "processGroup" ? "border-cyan-400 text-cyan-300" : "border-transparent text-gray-400 hover:text-gray-200"}`}>
            <FontAwesomeIcon icon={faLink} /> Process & Group Mapping
          </button>
          <button onClick={() => { setActiveTab("stateGroup"); setCurrentPage(1); setSearch(""); }}
            className={`px-6 py-3 font-medium whitespace-nowrap flex items-center gap-2 border-b-2 ${activeTab === "stateGroup" ? "border-cyan-400 text-cyan-300" : "border-transparent text-gray-400 hover:text-gray-200"}`}>
            <FontAwesomeIcon icon={faLink} /> State & Group Mapping
          </button>
          <button onClick={() => { setActiveTab("raciPerson"); setCurrentPage(1); setSearch(""); }}
            className={`px-6 py-3 font-medium whitespace-nowrap flex items-center gap-2 border-b-2 ${activeTab === "stateVariable" ? "border-cyan-400 text-cyan-300" : "border-transparent text-gray-400 hover:text-gray-200"}`}>
            <FontAwesomeIcon icon={faLink} /> RACI & Person Mapping
          </button>
        </div>

        {/* Controls */}
        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <div className="flex gap-3">
            <button onClick={loadSample} className="flex items-center gap-2 text-xs lg:text-sm w-32 lg:w-40 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium">
              <FontAwesomeIcon icon={faPlus} /> Load Sample
            </button>
            <button onClick={handleAddNew} className="flex items-center gap-2 text-xs lg:text-sm w-32 lg:w-40 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium">
              <FontAwesomeIcon icon={faPlus} /> New Mapping
            </button>
          </div>

          <div className="relative w-full">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder={`Search ${activeTab === "processGroup" ? "Process" : activeTab === "stateGroup" ? "State" : "Variable"}...`}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-cyan-500/60"
            />
          </div>
        </div>

        {/* Cards Grid */}
        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-3 xl:grid-cols-4">
          {paginated.map((item) => (
            <div key={item.id} className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-cyan-600/50 transition-colors group">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-base text-white group-hover:text-cyan-300">
                    {activeTab === "processGroup" && `${item.processName} → ${item.groupName}`}
                    {activeTab === "stateGroup" && `${item.stateName} → ${item.groupName}`}
                    {activeTab === "stateVariable" && `${item.stateName} → ${item.variableName}`}
                  </h3>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.active === "Yes" ? "bg-emerald-600" : "bg-red-600"}`}>
                  {item.active === "Yes" ? "Active" : "Inactive"}
                </span>
              </div>

              <p className="mt-4 text-sm text-gray-300 line-clamp-3">
                {activeTab === "processGroup" && item.processGroupRemarks}
                {activeTab === "stateGroup" && item.stateGroupRemarks}
                {activeTab === "stateVariable" && item.stateVariableRemarks}
              </p>

              <div className="mt-4 text-xs text-gray-500">
                <strong>Client:</strong> {item.client}
              </div>

              <div className="mt-6 flex gap-2">
                <button onClick={() => handleOpen(item)} className="flex-1 text-xs lg:text-sm w-40 lg:w-56 py-2 bg-gradient-to-r from-cyan-700 to-teal-700 hover:from-cyan-600 hover:to-teal-600 rounded-lg font-medium flex items-center justify-center gap-2">
                  <FontAwesomeIcon icon={faFolderOpen} /> Open
                </button>
                <button onClick={() => handleEdit(item)} className="w-12 p-2 bg-gray-700 hover:bg-gray-600 rounded-lg" title="Edit">
                  <FontAwesomeIcon icon={faEdit} className="text-yellow-400" />
                </button>
                <button onClick={() => handleDelete(item.id)} className="w-12 p-2 bg-gray-700 hover:bg-gray-600 rounded-lg" title="Delete">
                  <FontAwesomeIcon icon={faTrash} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}

          {paginated.length === 0 && (
            <div className="col-span-full py-12 text-center text-gray-500">
              {search ? "No records match your search" : `No ${activeTab === "processGroup" ? "Process Group" : activeTab === "stateGroup" ? "State Group" : "State Variable"} Mappings yet.`}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-sm">Page <strong>{currentPage}</strong> of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}

        {/* Modal - Dynamic based on tab */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg border border-gray-700 max-h-[92vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-cyan-300">
                  {editingItem ? "Edit" : "New"} {modalType === "processGroup" ? "Process Group Mapping" : modalType === "stateGroup" ? "State Group Mapping" : "State Variable Mapping"}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white w-12 text-2xl">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {modalType === "processGroup" && (
                  <>
                    <div>
                      <label className="block text-sm mb-1.5">Process Name <span className="text-red-400">*</span></label>
                      <input required value={formData.processName} onChange={e => setFormData({...formData, processName: e.target.value})} className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg" placeholder="e.g. CSAT Process" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1.5">Group Name <span className="text-red-400">*</span></label>
                      <input required value={formData.groupName} onChange={e => setFormData({...formData, groupName: e.target.value})} className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg" placeholder="e.g. Branch" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1.5">Process Group Remarks <span className="text-red-400">*</span></label>
                      <textarea value={formData.processGroupRemarks} onChange={e => setFormData({...formData, processGroupRemarks: e.target.value})} rows={3} className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg" />
                    </div>
                  </>
                )}

                {modalType === "stateGroup" && (
                  <>
                    <div>
                      <label className="block text-sm mb-1.5">State Name <span className="text-red-400">*</span></label>
                      <input required value={formData.stateName} onChange={e => setFormData({...formData, stateName: e.target.value})} className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg" placeholder="e.g. CAPA Initiated" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1.5">Group Name <span className="text-red-400">*</span></label>
                      <input required value={formData.groupName} onChange={e => setFormData({...formData, groupName: e.target.value})} className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg" placeholder="e.g. Branch" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1.5">State Group Remarks <span className="text-red-400">*</span></label>
                      <textarea value={formData.stateGroupRemarks} onChange={e => setFormData({...formData, stateGroupRemarks: e.target.value})} rows={3} className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg" />
                    </div>
                  </>
                )}

                {modalType === "stateVariable" && (
                  <>
                    <div>
                      <label className="block text-sm mb-1.5">State Name <span className="text-red-400">*</span></label>
                      <input required value={formData.stateName} onChange={e => setFormData({...formData, stateName: e.target.value})} className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg" placeholder="e.g. CAPA Initiated" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1.5">Variable Name <span className="text-red-400">*</span></label>
                      <input required value={formData.variableName} onChange={e => setFormData({...formData, variableName: e.target.value})} className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg" placeholder="e.g. AWB Number" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1.5">State Variable Remarks <span className="text-red-400">*</span></label>
                      <textarea value={formData.stateVariableRemarks} onChange={e => setFormData({...formData, stateVariableRemarks: e.target.value})} rows={3} className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg" />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm mb-1.5">Client <span className="text-red-400">*</span></label>
                  <input required value={formData.client} onChange={e => setFormData({...formData, client: e.target.value})} className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg" placeholder="e.g. 21st Century Financial Services" />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Active <span className="text-red-400">*</span></label>
                  <select value={formData.active} onChange={e => setFormData({...formData, active: e.target.value})} className="w-full h-12 px-4 bg-gray-700 border border-gray-600 rounded-lg">
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <button type="button" onClick={() => setShowModal(false)} className="text-xs lg:text-sm w-20 lg:w-32 px-2 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg">Cancel</button>
                  <button type="submit" className="px-8 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium">
                    {editingItem ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-8">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href="/about-us" className="hover:text-teal-300">About</a>
            <a href="/privacy-policy" className="hover:text-teal-300">Privacy Policy</a>
            <a href="/terms-of-use" className="hover:text-teal-300">Terms of Use</a>
            <a href="/contact-us" className="hover:text-teal-300">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProcessifydMappings;